import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

const HOST_SELECTOR = '[data-kc-cart-recommendations]';
const RECENT_LABEL = 'Récemment consultés';
const SECTION_ID = 'kc-cart-recent-recommendations';
let recentMarkupPromise;

const getRecentMarkup = async () => {
  if (recentMarkupPromise) return recentMarkupPromise;
  const ids = RecentlyViewed.getProducts();
  if (!Array.isArray(ids) || ids.length === 0) return null;

  const url = new URL(Theme.routes.search_url || '/search', window.location.origin);
  url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
  url.searchParams.set('type', 'product');
  url.searchParams.set('options[prefix]', 'last');

  recentMarkupPromise = sectionRenderer.getSectionHTML(SECTION_ID, false, url).catch(() => null);
  return recentMarkupPromise;
};

const hydrateHost = async (host) => {
  if (!(host instanceof HTMLElement) || host.dataset.kcRecentHydrated === 'true') return;
  host.dataset.kcRecentHydrated = 'true';

  const fallbackCards = Array.from(host.querySelectorAll('[data-kc-recommendation-card][data-kc-source="fallback"]'));
  if (fallbackCards.length === 0) return;

  const markup = await getRecentMarkup();
  if (!markup) return;

  const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
  const compact = host.dataset.kcCompact === 'true';
  const existingIds = new Set(
    Array.from(host.querySelectorAll('[data-kc-recommendation-card][data-kc-product-id]'))
      .map((card) => card.dataset.kcProductId)
      .filter(Boolean),
  );

  const variant = compact ? 'mini' : 'full';
  const recentOrder = RecentlyViewed.getProducts();
  const orderIndex = new Map(recentOrder.map((id, index) => [String(id), index]));
  const recentCards = Array.from(documentFragment.querySelectorAll(`[data-kc-cart-recent-variant="${variant}"]`))
    .map((wrapper) => wrapper.querySelector('[data-kc-recommendation-card][data-kc-product-id]'))
    .filter((card) => card && !existingIds.has(card.dataset.kcProductId))
    .sort((a, b) => (orderIndex.get(String(a.dataset.kcProductId)) ?? 999) - (orderIndex.get(String(b.dataset.kcProductId)) ?? 999));

  if (recentCards.length === 0) return;

  let replaced = 0;
  fallbackCards.forEach((fallbackCard) => {
    const recentCard = recentCards[replaced];
    if (!recentCard) return;
    fallbackCard.replaceWith(recentCard.cloneNode(true));
    replaced += 1;
  });

  // If the deterministic server fallback was the leading source and it was replaced
  // by Recently Viewed, keep the visible merchandising label honest as well.
  if (replaced > 0 && host.dataset.kcContextSource === 'fallback') {
    const compactLabel = host.querySelector('.kc-cart-contextual-mini__head > span');
    const fullLabel = host.querySelector('.kc-cart-contextual__eyebrow');
    const fullCopy = host.querySelector('.kc-cart-contextual__head p');
    if (compactLabel) compactLabel.textContent = RECENT_LABEL;
    if (fullLabel) fullLabel.textContent = RECENT_LABEL;
    if (fullCopy) fullCopy.textContent = 'Retrouvez des produits que vous avez consultés récemment.';
    host.dataset.kcContextSource = 'recent';
  }
};

const hydrate = (root = document) => {
  root.querySelectorAll?.(HOST_SELECTOR).forEach((host) => void hydrateHost(host));
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => hydrate(document), { once: true });
} else {
  hydrate(document);
}

document.addEventListener('shopify:section:load', (event) => hydrate(event.target));

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches?.(HOST_SELECTOR)) void hydrateHost(node);
      hydrate(node);
    }
  }
});
observer.observe(document.documentElement, { childList: true, subtree: true });
